import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TablePagination,
} from '@mui/material';
import { useLocation, useHistory } from 'react-router-dom';

import { useConfirm } from 'material-ui-confirm';
import { toast } from 'react-toastify';

import Row from './Rows/Row';
import { filtroMateriaPrima } from '../../../../../helpers/FilterFunctions';
import ReservaMateria from './ReservaMateria/ReservaMateria';
import { Legenda } from '../../../../../components/Legenda';

export default function TabelaResultados({
  listaOrdens,
  listaMateriasPrima,
  atualizaMateriaPrima,
  solicitaCompra,
}) {
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const confirm = useConfirm();

  // ── Paginação persistida na URL ──────────────────────────────────────────

  const page = parseInt(searchParams.get('page') || '0', 10);
  const rowsPerPage = parseInt(searchParams.get('rowsPerPage') || '200', 10);

  // Função auxiliar para atualizar a URL
  const setSearchParams = (params, options = {}) => {
    const newParams = new URLSearchParams(params).toString();
    if (options.replace) {
      history.replace({ search: newParams });
    } else {
      history.push({ search: newParams });
    }
  };

  const totalRows = listaOrdens?.length ?? 0;
  const lastValidPage = Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);

  useEffect(() => {
    if (totalRows > 0 && page > lastValidPage) {
      setSearchParams({ page: lastValidPage, rowsPerPage }, { replace: true });
    }
  }, [page, lastValidPage, totalRows]);

  const safePage = Math.min(page, lastValidPage);

  const handleChangePage = (_, newPage) => {
    setSearchParams({ page: newPage, rowsPerPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setSearchParams({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  // ── Estado local (não precisa persistir) ────────────────────────────────
  const [openDialogSeparaMateria, setOpenDialogSeparaMateria] = useState(false);
  const [producaoSelecionada, setProducaoSelecionada] = useState('');
  const [totalRiscador, setTotalRiscador] = useState(0);
  const [totalImpressora, setTotalImpressora] = useState(0);
  const [modeloCaixa, setModeloCaixa] = useState('');
  const [materiaVincada, setMateriaVincada] = useState('');
  const [qtdPedido, setQtdPedido] = useState('');
  const [filtredMateriasPrima, setFiltredMateriasPrima] = useState([]);

  const fechaDialogSeparaMateria = () => {
    setOpenDialogSeparaMateria(false);
    setTotalImpressora(0);
    setTotalRiscador(0);
    setModeloCaixa('');
  };

  const filtroReservaMateria = (
    producao,
    comprimento,
    largura,
    gramatura,
    qtdPedido,
    materiaPedida,
    qtdePedida,
    divisorComprimento,
    divisorLargura,
    vincada,
    modeloCaixa,
    coeficienteCaixa,
    duplicada,
  ) => {
    if (duplicada !== 0) {
      confirm({
        title: 'Alerta!',
        description:
          'Existe uma ou mais ordens vinculadas a esse pedido, Verifique!',
        confirmationText: 'OK',
        cancellationText: '',
      });
    }

    if (materiaPedida !== null) {
      const materialPedido = listaMateriasPrima.find(
        (materia) => materia.codigo == materiaPedida,
      );

      if (!materialPedido) {
        toast.error('Material não encontrado');
        return;
      }

      setFiltredMateriasPrima([
        {
          ...materialPedido,
          qtde_chapas: qtdePedida,
          qtde_caixasPedido: qtdPedido,
        },
      ]);
    } else {
      filtroMateriaPrima(
        listaMateriasPrima,
        comprimento,
        largura,
        gramatura,
        qtdPedido,
        divisorComprimento,
        divisorLargura,
        coeficienteCaixa,
      )
        .then(setFiltredMateriasPrima)
        .catch(console.error);
    }

    setTotalRiscador(largura);
    setTotalImpressora(comprimento);
    setProducaoSelecionada(producao);
    setOpenDialogSeparaMateria(true);
    setMateriaVincada(vincada);
    setModeloCaixa(modeloCaixa);
    setQtdPedido(qtdPedido);
  };

  const ordens = listaOrdens ?? [];
  const visibleRows = ordens.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage,
  );

  return (
    <Box>
      {listaOrdens && (
        <ReservaMateria
          openDialogSeparaMateria={openDialogSeparaMateria}
          onClose={fechaDialogSeparaMateria}
          producaoSelecionada={producaoSelecionada}
          listaMateriasPrima={filtredMateriasPrima}
          atualizaMateriaPrima={atualizaMateriaPrima}
          totalRiscador={totalRiscador}
          totalImpressora={totalImpressora}
          materiaVincada={materiaVincada}
          modeloCaixa={modeloCaixa}
          qtdPedido={qtdPedido}
        />
      )}

      <TableContainer component={Paper}>
        <Table stickyHeader aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={2}>
                <Legenda />
              </TableCell>
              <TableCell>Pedido</TableCell>
              <TableCell>Emissão</TableCell>
              <TableCell>Entrega</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Caixa</TableCell>
              <TableCell>Comp.</TableCell>
              <TableCell>Larg.</TableCell>
              <TableCell>Alt.</TableCell>
              <TableCell>Chapa</TableCell>
              <TableCell>Gramat.</TableCell>
              <TableCell>Qtde</TableCell>
              <TableCell>Kilo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((ordem, index) => (
              <Row
                key={index}
                ordem={ordem}
                filtroReservaMateria={filtroReservaMateria}
                solicitaCompra={solicitaCompra}
              />
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 150, 200]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={safePage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}
